/**
 * Task 260 — PURE summary of real creative-analysis state for the Creative
 * Intelligence stage 4 (replaces the Milestone-A `currentAnalysisStatus()`
 * placeholder that hard-returned 'not_run').
 *
 * Input is the `get-creative-analysis-status` edge function's client-scoped
 * read (jobs + persisted analyses, both RLS-gated server-side). This module
 * derives the display facts ONLY — no React, no I/O, no AI — so it is
 * unit-testable directly (same pattern as creativeWorkflowStages.ts).
 */

export interface CreativeAnalysisJobLite {
  readonly status: 'queued' | 'running' | 'succeeded' | 'failed' | string;
  readonly creative_asset_id: string | null;
  readonly error_code: string | null;
  readonly finished_at: string | null;
}

export interface CreativeAnalysisLite {
  readonly id: string;
  readonly creative_asset_id: string | null;
  readonly analyzed_at: string;
}

export interface CreativeAnalysisStatusSummary {
  /** Real analyses exist (persisted = succeeded; the table has no status column). */
  readonly ready: boolean;
  readonly analysesCount: number;
  /** Distinct assets with at least one analysis. */
  readonly analyzedAssetsCount: number;
  /** ISO timestamp of the newest analysis, or null. */
  readonly latestAnalyzedAt: string | null;
  readonly failedJobsCount: number;
  /** queued + running. */
  readonly openJobsCount: number;
}

export function summarizeCreativeAnalysisStatus(
  jobs: ReadonlyArray<CreativeAnalysisJobLite>,
  analyses: ReadonlyArray<CreativeAnalysisLite>,
): CreativeAnalysisStatusSummary {
  const assetIds = new Set<string>();
  let latest: string | null = null;
  for (const a of analyses) {
    if (a.creative_asset_id !== null) assetIds.add(a.creative_asset_id);
    if (latest === null || a.analyzed_at > latest) latest = a.analyzed_at;
  }
  let failed = 0;
  let open = 0;
  for (const j of jobs) {
    if (j.status === 'failed') failed += 1;
    if (j.status === 'queued' || j.status === 'running') open += 1;
  }
  return {
    ready: analyses.length > 0,
    analysesCount: analyses.length,
    analyzedAssetsCount: assetIds.size,
    latestAnalyzedAt: latest,
    failedJobsCount: failed,
    openJobsCount: open,
  };
}
